import React from 'react';
import { connect } from 'react-redux';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { BrowserTitleService } from '~commonInfra/services/BrowserTitleService';
import { ServerUrls } from '~commonInfra/url/ServerUrls';
import { SessionServerSelectors, SessionUserSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { ConnectedApplicationSubspaceRouter } from '~src/connectedApplications/components/ConnectedApplicationSubspaceRouter';
import { ConnectedResourceGovernanceSubspace } from '~src/schedule/components/ConnectedResourceGovernanceSubspace';
import { ResourceGovernanceSubspaceName } from '~src/schedule/components/ResourceGovernanceSubspace';
import {
  IntegrationsSiteSettingsSubspaceComponent,
  IntegrationsSubspaceName
} from '~src/settings/IntegrationsSettings/IntegrationsSiteSettingsSubspace';
import { MobileSettingsSubspaceName } from '~src/settings/mobile-settings/MobileSecuritySettingsShared';
import { ServerSettingsInstance } from '~src/settings/services/ServerSettings';
import { Sites } from '~src/siteManagement/services/resources/Sites';
import { Subspace, TabbedSpace } from '~src/spacesPlatform/components/spaceComponents/TabbedSpace';
import { selectSpaceInfo } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';
import { ServerSettingsSubspaceName } from '~src/spacesPlatform/state/SpaceInfo';
import { SiteRoles } from '~src/userManagement/users/serverApi/types/ISiteRole';

import { AppState } from '../../../../bootstrap/redux/state/AppState';
import { Messages } from '../../../../codegen/Localize';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../../../sessionManagement/SessionManagementService';
import { AddSiteSettingsSubspace } from './addSite/AddSiteSettingsSubspace';
import { ExtensionsServerSettingsSubspaceComponent } from './extensions/ExtensionsServerSettingsSubspace';
import { GeneralServerSettingsSubspace } from './GeneralServerSettingsSubspace';
import { LicensesSettingsSubspace } from './licenses/LicensesSettingsSubspace';
import { MobileServerSettingsSubspaceComponent } from './mobile/MobileServerSettingsSpace';
import { CustomizationSettingsSubspace } from './welcomeChannel/CustomizationSettingsSubspace';



export interface ServerSettingsSpaceOwnProps {
  readonly subspaceName: ServerSettingsSubspaceName;
}

export interface ServerSettingsServicesProps {
  readonly server: SessionManagementService;
}

export interface ServerSettingsStateProps {
  readonly isSessionMultiSite: boolean;
  readonly isOnlineEnvironment: boolean;
  readonly stateSubspaceName: ServerSettingsSubspaceName;
}

export type ServerSettingsNonServicesProps = ServerSettingsSpaceOwnProps & ServerSettingsStateProps;
export type ServerSettingsSpaceProps = ServerSettingsNonServicesProps & ServerSettingsServicesProps;

export class ServerSettingsSpace extends React.PureComponent<ServerSettingsSpaceProps> {

  private static SubSpaceToTitle: { [P in ServerSettingsSubspaceName]: () => string } = {
    settings: Messages.serverSettings_general,
    licenses: Messages.serverSettings_licenses,
    extensions: Messages.serverSettings_extensions,
    addsite: Messages.serverSettings_addSite,
    customization: Messages.serverSettings_customization,
    integrations: Messages.serverSettings_integrations,
    resourcesgovernance: Messages.resourceGovernanceTitle,
    connectedApplications: Messages.serverSettings_connectedApplications,
    mobile: Messages.serverSettings_mobile,
  };

  public componentDidMount() {
    const title = ServerSettingsSpace.SubSpaceToTitle[this.props.subspaceName]();
    BrowserTitleService.setTitleWithTranslatedSpace({ translatedSpaceName: title });
  }

  public render() {
    const subspaces = getSettingsSubspaces(
      this.props.server,
      this.props.isSessionMultiSite,
      this.props.isOnlineEnvironment,
      this.props.subspaceName,
      this.props.stateSubspaceName
    );

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={this.props.subspaceName}
        urlForSubspace={this.getSettingsUrl}
        mastheadElement={null}
        testIdPrefix={'server-settings'}
        spaceItemName={''}
      />
    );
  }

  private static settingsUrlBySubspace: Record<ServerSettingsSubspaceName, () => string> = {
    settings: () => ServerUrls.getInstance().settingsUrl(),
    extensions: () => ServerUrls.getInstance().settingsExtensionsUrl(),
    addsite: () => ServerUrls.getInstance().addSiteUrl(),
    licenses: () => ServerUrls.getInstance().settingsLicensesUrl(),
    customization: () => ServerUrls.getInstance().settingsCustomizationUrl(),
    integrations: () => ServerUrls.getInstance().settingsIntegrationsUrl(),
    resourcesgovernance: () => ServerUrls.getInstance().settingsResourceLimitsUrl(),
    connectedApplications: () => ServerUrls.getInstance().settingsConnectedApplicationsUrl(),
    mobile: () => ServerUrls.getInstance().settingsMobileUrl(),
  };

  private getSettingsUrl = (subspaceName?: ServerSettingsSubspaceName) => {
    if (subspaceName) {
      return ServerSettingsSpace.settingsUrlBySubspace[subspaceName]();
    }
    return '';
  };
}

function getSettingsSubspaces(
  server: SessionManagementService,
  isSessionMultiSite: boolean,
  isOnlineEnvironment: boolean,
  subspaceName: ServerSettingsSubspaceName,
  stateSubspaceName: ServerSettingsSubspaceName
): ReadonlyArray<Subspace> {
  return _.compact<_.List<Subspace | false>>([
    {
      name: 'settings',
      tabLabel: Messages.general(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <GeneralServerSettingsSubspace
          server={server}
          isSessionMultiSite={isSessionMultiSite}
          serverSettingsService={ServerSettingsInstance}
          sites={Sites.instance}
        />
      )
    },
    {
      name: 'licenses',
      tabLabel: Messages.licenses(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <LicensesSettingsSubspace
        />
      )
    },
    FeatureFlags.extensionsSettingsTabEnabled && {
      name: 'extensions',
      tabLabel: Messages.extensionsSettings_title(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <ExtensionsServerSettingsSubspaceComponent
          server={server}
          isSessionMultiSite={isSessionMultiSite}
        />
      )
    },
    FeatureFlags.serverWelcomeChannelEnabled && {
      name: 'customization',
      tabLabel: Messages.customizationSetting_title(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <CustomizationSettingsSubspace />
      )
    },
    isIntegrationsTabEnabledForServerSettings() && {
      name: IntegrationsSubspaceName,
      tabLabel: Messages.integrations_settingsTabTitle(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <IntegrationsSiteSettingsSubspaceComponent
          siteName={undefined}
          urls={undefined}
          server={server}
          isSessionMultiSite={isSessionMultiSite}
          isSessionServerAdmin={true}
          isOnlineEnvironment={isOnlineEnvironment}
          userRole={SiteRoles.ServerAdministrator}
          sites={Sites.instance}
        />
      )
    },
    FeatureFlags.onPremiseGovernanceEnabled && {
      name: ResourceGovernanceSubspaceName,
      tabLabel: Messages.resourceGovernanceTitle(),
      shouldRenderTabContentCount: false,
      renderContent: () => {
        if (subspaceName === stateSubspaceName) {
          return <ConnectedResourceGovernanceSubspace />;
        }
      },
    },
    !isSessionMultiSite &&
    FeatureFlags.connectedApplicationsSettingsTabEnabled &&
    {
      name: 'connectedApplications',
      tabLabel: Messages.connectedApplications(),
      //TODO: Add contentCount -- contentCount: 1,
      shouldRenderTabContentCount: false,
      renderContent: () => {
        if (subspaceName === stateSubspaceName) {
          return <ConnectedApplicationSubspaceRouter />;
        }
      }
    },
    !isSessionMultiSite && {
      name: 'addsite',
      tabLabel: Messages.addASite(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <AddSiteSettingsSubspace />
      )
    },
    FeatureFlags.mobileSecuritySettingsEnabled && {
      name: MobileSettingsSubspaceName,
      tabLabel: Messages.mobileSettings_title(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <MobileServerSettingsSubspaceComponent
          server={server}
          isSessionMultiSite={isSessionMultiSite}
        />
      )
    },
  ]);
}

export const isIntegrationsTabEnabledForServerSettings = () => {
  const sessionManagementService = getSessionManagementServiceInstance();
  let singleSiteServer_SlackSettingsEnabled = sessionManagementService.isUserNotificationsPlatformEnabled() && sessionManagementService.isSingleSiteOnPremServer() && FeatureFlags.userNotificationSlackChannel;
  return FeatureFlags.publishingSalesforceBundlesEnabled || singleSiteServer_SlackSettingsEnabled;
};

export const ConnectedServerSettingsSpace: React.ComponentClass<ServerSettingsSpaceOwnProps & ServerSettingsServicesProps>
  = connect((state: AppState) => ({
    isSessionMultiSite: SessionUserSelectors.isSessionMultiSite(state),
    isOnlineEnvironment: SessionServerSelectors.isOnlineEnvironment(state),
    stateSubspaceName: selectSpaceInfo(state).subspaceName,
  }))(ServerSettingsSpace);
